import { cloneElement, createContext, isValidElement, useContext, useId } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export const DraftFieldsContext = createContext<Set<string>>(new Set());

type NativeControlProps = {
  id?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
};

export const inputBase =
  'form-prompt-control w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 shadow-sm focus:border-teal-600 focus:ring-2 focus:ring-teal-200 focus:outline-none transition';

export const SectionCard = ({
  title,
  description,
  icon: Icon,
  hint,
  children,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  hint?: string;
  children: ReactNode;
}) => (
  <section className="form-prompt-section-card space-y-5">
    <div className="form-prompt-section-head">
      {Icon && (
        <div className="form-prompt-section-icon">
          <Icon size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold text-slate-900 sm:text-lg">{title}</h2>
          {description && <TooltipIcon text={description} />}
        </div>

      </div>
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);

export const LockedArea = ({
  locked,
  children,
}: {
  locked: boolean;
  message?: string;
  children: ReactNode;
}) => (
  <div className={locked ? 'select-none opacity-45' : ''}>
    <fieldset disabled={locked} className="m-0 min-w-0 border-0 p-0">
      {children}
    </fieldset>
  </div>
);

export const TooltipIcon = ({ text }: { text: string }) => (
  <span className="group relative inline-flex items-center">
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-bold text-slate-500 outline-none transition group-focus-within:border-teal-500 group-focus-within:text-teal-700"
      aria-label={text}
      role="img"
      tabIndex={0}
    >
      i
    </span>
    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-max max-w-[220px] -translate-x-1/2 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 shadow-lg group-hover:block group-focus-within:block">
      {text}
    </span>
  </span>
);

export const Field = ({
  label,
  required,
  helper,
  tooltip,
  error,
  errorKey,
  dirty,
  trailing,
  children,
}: {
  label: string;
  required?: boolean;
  helper?: string;
  tooltip?: string;
  error?: string;
  errorKey?: string;
  dirty?: boolean;
  trailing?: ReactNode;
  children: ReactNode;
}) => {
  const generatedId = useId();
  const controlId = `ai-field-${generatedId.replace(/:/g, '')}`;
  const helperId = `${controlId}-help`;
  const errorId = `${controlId}-error`;
  const draftFields = useContext(DraftFieldsContext);
  const isDraft = dirty ?? (Boolean(errorKey) && draftFields.has(errorKey as string));
  const canAssociate = isValidElement(children) && typeof children.type === 'string' && ['input', 'select', 'textarea'].includes(children.type);
  const control = canAssociate ? (children as ReactElement<NativeControlProps>) : null;
  const renderedChildren = control
    ? cloneElement(control, {
        id: control.props.id || controlId,
        'aria-describedby': [helper ? helperId : '', error ? errorId : ''].filter(Boolean).join(' ') || undefined,
        'aria-invalid': Boolean(error),
      })
    : children;

  return (
    <div
      className={`form-prompt-field space-y-2${error ? ' is-error' : ''}${isDraft ? ' is-draft' : ''}`}
      data-error-key={errorKey}
    >
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={canAssociate ? controlId : undefined} className="text-sm font-semibold text-slate-800">{label}</label>
        {tooltip && <TooltipIcon text={tooltip} />}
        {required && <span className="text-rose-600" aria-label="Campo obrigatório">*</span>}
        {isDraft && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
            Rascunho
          </span>
        )}
      </div>
      {helper && <p id={helperId} className="text-xs text-slate-500">{helper}</p>}
      {renderedChildren}
      {trailing}
      {error && <p id={errorId} className="text-xs text-rose-600">{error}</p>}
    </div>
  );
};

export const BooleanToggle = ({
  value,
  onChange,
  yesLabel = 'Sim',
  noLabel = 'Não',
  disableYes = false,
  disableNo = false,
}: {
  value: boolean | null;
  onChange: (value: boolean) => void;
  yesLabel?: string;
  noLabel?: string;
  disableYes?: boolean;
  disableNo?: boolean;
}) => (
  <div className="form-prompt-boolean-toggle inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1">
    <button
      type="button"
      onClick={() => onChange(true)}
      disabled={disableYes}
      aria-pressed={value === true}
      className={`min-h-[44px] rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${
        value === true ? 'bg-[#2f7d72] text-white shadow-sm' : 'text-slate-600'
      }`}
    >
      {yesLabel}
    </button>
    <button
      type="button"
      onClick={() => onChange(false)}
      disabled={disableNo}
      aria-pressed={value === false}
      className={`min-h-[44px] rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${
        value === false ? 'bg-[#2f7d72] text-white shadow-sm' : 'text-slate-600'
      }`}
    >
      {noLabel}
    </button>
  </div>
);

export const ChoiceConsequences = ({
  yes,
  no,
}: {
  yes: string;
  no: string;
}) => (
  <div className="grid gap-2 text-xs leading-5 text-slate-600 sm:grid-cols-2">
    <p className="rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2">
      <strong className="text-emerald-800">Se escolher Sim:</strong> {yes}
    </p>
    <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <strong className="text-slate-800">Se escolher Não:</strong> {no}
    </p>
  </div>
);

export const Chip = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`min-h-[44px] rounded-full px-4 py-2 text-xs font-semibold transition ${
      active
        ? 'bg-[#2f7d72] text-white shadow-sm'
        : 'border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
    }`}
  >
    {label}
  </button>
);
